import { mapViewLocationTypeLabel } from '~/client/src/shared/components/MapViews/MapViewsSidebar/mapViewLocationTypeLabel'
import MapViewLocationIcon, {
  LOCATION_ICONS,
  OFFLOADING_EQUIPMENT_ICONS,
} from '~/client/src/shared/enums/SitemapAttributeIcon'
import {
  PictogramSlug,
  PICTOGRAMS,
  pictogramSlugOf,
} from '~/client/src/shared/icons/pictograms'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import { isInformationPointKind } from '~/client/src/shared/resources/domain/informationPointGroups'
import { LocationKind } from '~/client/src/shared/resources/domain/locationKind'

export interface IIconCatalog {
  struxhub: readonly MapViewLocationIcon[]
  custom: readonly PictogramSlug[]
}

export const LOCATION_ICON_CATALOG: IIconCatalog = {
  struxhub: LOCATION_ICONS,
  custom: PICTOGRAMS.map(p => p.slug),
}

export const EQUIPMENT_ICON_CATALOG: IIconCatalog = {
  struxhub: OFFLOADING_EQUIPMENT_ICONS,
  custom: [],
}

export const iconCatalogFor = (isEquipment: boolean): IIconCatalog =>
  isEquipment ? EQUIPMENT_ICON_CATALOG : LOCATION_ICON_CATALOG

// Map Setup's pallet names for the glyphs mapViewLocationTypeLabel only title-cases.
const PALLET_LABELS: Partial<
  Record<MapViewLocationIcon, (t: typeof Localization.translator) => string>
> = {
  [MapViewLocationIcon.InteriorPath]: t => t.interiorPath,
  [MapViewLocationIcon.InteriorDoor]: t => t.interiorDoor,
  [MapViewLocationIcon.Stairs]: t => t.stairway,
  [MapViewLocationIcon.Elevator]: t => t.elevator,
  [MapViewLocationIcon.Shaft]: t => t.shaft,
  [MapViewLocationIcon.Bathroom]: t => t.restrooms,
  [MapViewLocationIcon.Break]: t => t.breakText,
  [MapViewLocationIcon.Canteen]: t => t.canteen,
  [MapViewLocationIcon.Dumpster]: t => t.dumpster,
  [MapViewLocationIcon.Entrance]: t => t.access,
  [MapViewLocationIcon.HandWash]: t => t.washArea,
  [MapViewLocationIcon.Medical]: t => t.medicalArea,
  [MapViewLocationIcon.MeetingPoint]: t => t.musterPoint,
  [MapViewLocationIcon.Parking]: t => t.parking,
  [MapViewLocationIcon.Smoking]: t => t.smokingArea,
  [MapViewLocationIcon.Temperature]: t => t.covidCheckpoint,
  [MapViewLocationIcon.Tent]: t => t.breakArea,
  [MapViewLocationIcon.Toilet]: t => t.toilet,
  [MapViewLocationIcon.ElectricalRoom]: t => t.electricalRoom,
  [MapViewLocationIcon.Crane]: t => t.crane,
  [MapViewLocationIcon.Hoist]: t => t.hoist,
  [MapViewLocationIcon.AerialLift]: t => t.aerialLift,
  [MapViewLocationIcon.Gradall]: t => t.gradall,
  [MapViewLocationIcon.ForkLift]: t => t.forkLift,
}

const KIND_LABELS: Readonly<
  Record<string, (t: typeof Localization.translator) => string>
> = {
  [LocationKind.ProjectLocation]: t => t.projectLocation,
  [LocationKind.Building]: t => t.building,
  [LocationKind.Zone]: t => t.zone,
  [LocationKind.Staging]: t => t.staging,
  [LocationKind.Level]: t => t.level,
  [LocationKind.Area]: t => t.area,
  [LocationKind.Route]: t => t.route,
  [LocationKind.Gate]: t => t.gate,
  [LocationKind.InteriorPath]: t => t.interiorPath,
  [LocationKind.InteriorDoor]: t => t.interiorDoor,
  [LocationKind.VerticalObject]: t => t.verticalObject,
  general_vertical_object: t => t.verticalObject,
  stairway: t => t.stairway,
  elevator: t => t.elevator,
  shaft: t => t.shaft,
  [LocationKind.InformationPoints]: t => t.informationPoints,
}

/** A location kind's name, as Map Setup's palette spells it. */
export function kindLabel(kind: string): string {
  const t = Localization.translator
  return isInformationPointKind(kind)
    ? t.informationPointTypes[kind]
    : (KIND_LABELS[kind]?.(t) ?? kind)
}

export function iconLabel(
  iconName: string,
  t: typeof Localization.translator = Localization.translator,
): string {
  if (!iconName) {
    return ''
  }
  const slug = pictogramSlugOf(iconName)
  if (slug) {
    return t.pictograms.names[slug]
  }
  return (
    t.glyphNames[iconName as keyof typeof t.glyphNames] ??
    PALLET_LABELS[iconName as MapViewLocationIcon]?.(t) ??
    mapViewLocationTypeLabel(iconName as MapViewLocationIcon, t)
  )
}
